// Third party
import React from 'react';
import { SortingRule } from 'react-table';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { LocationDescriptorObject } from 'history';

// Components
import FundSearchInput from './FundSearchInput';
import FundSearchResult from './FundSearchResult';
import BenchmarkSearchInput from './BenchmarkSearchInput';
import BenchmarkSearchResult from './BenchmarkSearchResult';
import ProxySearchInput from './ProxySearchInput';
import { withErrorBoundary } from '@/views/components/errors';
import { SearchResultColumns } from './columns-definition/SearchResult';

// Selectors
import { selectAdvancedSearchShelfStatusedFund } from '@/redux/modules/shelf-management/selectors';

// Actions
import { ReduxState } from '@/redux/modules/types';
import {
  searchAllFunds,
  searchAllBenchmarks,
  updateSearchSorting,
  loadSearchFilters,
  resetSearch,
  clearResults,
  setFocusedFund,
  setFocusedBenchmark,
  loadMoreFundResults,
  setPreloadQuery,
  setSelectedBenchmarks,
  loadMoreBenchmarkResults,
} from '@/redux/modules/advanced-search';
import { toggleSavedFund } from '@/redux/modules/funds/saved-funds';
import { toggleSavedBenchmark } from '@/redux/modules/comparison-benchmarks/saved-benchmarks';
import { addFundsToComparison, addBenchmarksToComparison, ADD_FUNDS_TO_COMPARISON } from '@/redux/modules/comparison';
import { resetPortfolio, addFundsToPortfolio, ADD_FUNDS_TO_PORTFOLIO } from '@/redux/modules/edit-portfolio';
import { addSearchItemLog } from '@/redux/modules/activity-log';
import { addBenchmarksToPortfolio, loadNewBenchmark } from '@/redux/modules/edit-benchmark';
import { clearComplianceAlternatives } from '@/redux/modules/compliance-alternatives';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import { SEARCH_ITEM_SOURCE, SEARCH_ITEM_DEST } from '@/utils/activity-log';
import { FormattedMessage } from '@/utils/i18n';
import { ControlItem, Tabs } from '../navigation';
import { ADVANCED_SEARCH_OPTIONS, COMPARISON_ITEM_TYPES, PROXY_OPTION, FUND_SEARCH_TYPES, NAV_PATHS } from '@/utils/constants';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';
import { FundSearchState } from '@/redux/modules/types/fund-search';
import { BenchmarkSearchState } from '@/redux/modules/types/benchmark-search';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';
import SearchOptions from '@/types/search-options';
import Fund from '@/types/fund';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { MultiselectAction } from '@/types/data-table';
import AdvancedSearchItem from '@/types/advanced-search-item';

// Legacy Support
import withLegacyVersion from '../hocs/withLegacyVersion';
import LegacyAdvancedSearch from './LegacyAdvancedSearch';

const FUND_COLUMNS = [
  SearchResultColumns.INLINE_ADD,
  SearchResultColumns.NAME,
  SearchResultColumns.UNIVERSE,
  SearchResultColumns.CLASSIFICATION,
  SearchResultColumns.ONE_YEAR,
  SearchResultColumns.THREE_YEARS,
  SearchResultColumns.FIVE_YEARS,
  SearchResultColumns.INC,
  SearchResultColumns.MER,
  SearchResultColumns.BETA,
  SearchResultColumns.STD_DEV,
  SearchResultColumns.IS_SAVED,
  SearchResultColumns.IS_FOCUSED,
];

const TABLE_COLUMNS_MAP: Partial<Record<ADVANCED_SEARCH_OPTIONS, SearchResultColumns[]>> = {
  [ADVANCED_SEARCH_OPTIONS.FUNDS]: FUND_COLUMNS,
  [ADVANCED_SEARCH_OPTIONS.PROXIES]: FUND_COLUMNS,
  [ADVANCED_SEARCH_OPTIONS.BENCHMARKS]: [
    SearchResultColumns.INLINE_ADD,
    SearchResultColumns.NAME,
    SearchResultColumns.CLASSIFICATION,
    SearchResultColumns.THREE_YEARS,
    SearchResultColumns.FIVE_YEARS,
    SearchResultColumns.TEN_YEARS,
    SearchResultColumns.YT,
    SearchResultColumns.STD_DEV,
    SearchResultColumns.IS_SAVED,
  ],
};

type DispatchProps = {
  searchAllFunds: (options: SearchOptions, sorting?: SortingRule[]) => Promise<Fund[]>;
  searchAllBenchmarks: (options: SearchOptions, sorting?: SortingRule[]) => Promise<ComparisonBenchmark[]>;
  updateSearchSorting: (sorting: SortingRule[], selectedOption: ADVANCED_SEARCH_OPTIONS) => void;
  loadSearchFilters: () => Promise<void>;
  toggleSavedFund: (fund: Fund) => Promise<Fund[]>;
  toggleSavedBenchmark: (benchmark: ComparisonBenchmark) => Promise<ComparisonBenchmark[]>;
  resetSearch: () => void;
  clearResults: () => void;
  addFundsToComparison: (funds: Fund[]) => Promise<void>;
  resetPortfolio: () => void;
  setFocusedFund: (fund?: Fund) => void;
  addFundsToPortfolio: (funds: Fund[]) => Promise<void[]>;
  loadMoreFundResults: () => Promise<void>;
  setPreloadQuery: (query: string) => void;
  addSearchItemLog: (id: string, type: string, source: string, dest: string) => void;
  setSelectedBenchmarks: (benchmarks: ComparisonBenchmark[]) => void;
  loadMoreBenchmarkResults: () => Promise<void>;
  setFocusedBenchmark: (benchmark?: ComparisonBenchmark) => void;
  addBenchmarksToComparison: (benchmarks: ComparisonBenchmark[]) => Promise<void>;
  addBenchmarksToPortfolio: (benchmarks: ComparisonBenchmark[]) => Promise<void[]>;
  loadNewBenchmark: () => void;
  onChangeOption?: (option: ADVANCED_SEARCH_OPTIONS) => void;
  clearComplianceAlternatives: () => void;
};

type FunctionProps = {
  onRowClick?: (id: string) => void;
  onCompareAlternatives?: () => void;
};

type StateProps = {
  fund: FundSearchState;
  benchmark: BenchmarkSearchState;
  hasMore?: boolean;
  loadingResults: boolean;
  loadingFilters: boolean;
  savedFunds: Fund[];
  savedBenchmarks: ComparisonBenchmark[];
  totalCount?: number;
  preLoadQuery: string;
  searchQuery: string;
  useWindowScrollAncestor?: boolean;
  user?: Nullable<User>;
  minimizedFiltersToShow?: number;
  customTableActions?: JSX.Element[] | MultiselectAction<AdvancedSearchItem>[];
  addingToPortfolio: boolean;
  addingToComparison: boolean;
};

type LegacyProps = {
  hideSaveFund?: boolean;
  isV2?: boolean;
  overrideShowArrow?: boolean;
  onArrowClick?: (fund: Fund) => void;
  setSelectedFunds?: (funds: Fund[]) => void;
};

type Props = DispatchProps &
  StateProps &
  FunctionProps &
  Partial<RouteComponentProps> & {
    classicVersionOnly?: boolean;
    className?: string;
    insideModal?: boolean;
    query?: string;
    selectedOption?: ADVANCED_SEARCH_OPTIONS;
    hideCompareAllAlternatives?: boolean;
    hideSearchOptions?: boolean;
    compact?: boolean;
    canSelect?: boolean;
    proxyOption?: PROXY_OPTION;
    showInlineAdd?: boolean;
    onAddItem: (fund: Fund) => void;
  } & LegacyProps;

type State = {
  redirect?: string | LocationDescriptorObject;
  selectedOption: ADVANCED_SEARCH_OPTIONS;
  sorting?: SortingRule[];
};

class AdvancedSearch extends React.Component<Props, State> {
  static defaultProps = {
    onAddItem: () => {},
  };

  private getHasData = (selectedOption: ADVANCED_SEARCH_OPTIONS, fundLength: number, benchmarkLength: number) => {
    return selectedOption === ADVANCED_SEARCH_OPTIONS.FUNDS ? !!fundLength : !!benchmarkLength;
  };

  constructor(props: Props) {
    super(props);
    this.props.loadSearchFilters();
    this.props.resetSearch();
    this.state = {
      redirect: undefined,
      selectedOption: props.selectedOption || ADVANCED_SEARCH_OPTIONS.FUNDS,
    };
  }

  componentWillUnmount() {
    this.props.clearResults();
  }

  getSearchOptions = () => {
    const { user } = this.props;

    if (!user) {
      return [];
    }

    return [
      {
        value: ADVANCED_SEARCH_OPTIONS.FUNDS,
        i18nKey: 'Search.Nav.Funds',
      },
      {
        value: ADVANCED_SEARCH_OPTIONS.BENCHMARKS,
        i18nKey: 'Search.Nav.Benchmarks',
        hidden: !!user && !userHasPermission(user, PERMISSIONS.BENCHMARKS),
      },
    ].filter(o => !o.hidden);
  };

  onChangeOption = (selectedOption: ADVANCED_SEARCH_OPTIONS) => {
    const { onChangeOption, insideModal, history } = this.props;

    this.props.clearResults();
    this.setState({ sorting: undefined });

    if (onChangeOption) {
      onChangeOption(selectedOption);
    }

    this.setState({ selectedOption });
    if (!insideModal && history) {
      history.push(`/search/${selectedOption}`);
    }
  };

  onFundSearch = (options: SearchOptions) => {
    this.props.searchAllFunds(options, this.state.sorting);
  };

  onBenchmarkSearch = (options: SearchOptions) => {
    this.props.searchAllBenchmarks(options, this.state.sorting);
  };

  onProxySearch = (options: SearchOptions) => {
    const { proxyOption } = this.props;

    if (!proxyOption) {
      return;
    }

    const searchOptions = {
      ...options,
      types: [FUND_SEARCH_TYPES.BENCHMARK_PROXY],
    };

    this.props.searchAllFunds(searchOptions, this.state.sorting);
  };

  onSortingChange = (sort: SortingRule[]) => {
    const { selectedOption } = this.state;
    this.setState(prevState => ({
      ...prevState,
      sorting: sort,
    }));
    this.props.updateSearchSorting(sort, selectedOption);
  };

  onToggleSavedFund = (row: Fund) => {
    const {
      fund: { funds },
    } = this.props;
    if (!funds) {
      return;
    }

    const fund = funds.find(f => f._id === row._id);
    if (!fund) {
      return;
    }

    this.props.toggleSavedFund(fund);
  };

  onToggleSavedBenchmark = (row: ComparisonBenchmark) => {
    const {
      benchmark: { benchmarks },
    } = this.props;
    if (!benchmarks) {
      return;
    }

    const benchmark = benchmarks.find(f => f._id === row._id);
    if (!benchmark) {
      return;
    }

    this.props.toggleSavedBenchmark(benchmark);
  };

  onStartComparison = (selectedFunds: Fund[]) => {
    const { location } = this.props;

    this.props.addFundsToComparison(selectedFunds).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  onAddFundsToPortfolio = (selectedFunds: Fund[]) => {
    this.props.resetPortfolio();
    this.props.addFundsToPortfolio(selectedFunds).then(() => {
      for (const fund of selectedFunds) {
        this.props.addSearchItemLog(fund._id, COMPARISON_ITEM_TYPES.FUND, SEARCH_ITEM_SOURCE.ADVANCED_SEARCH, SEARCH_ITEM_DEST.PORTFOLIO);
      }

      this.setState({ redirect: '/models?newPortfolio=true' });
    });
  };

  onStartBenchmarksComparison = () => {
    const {
      benchmark: { selectedBenchmarks },
      location,
    } = this.props;

    this.props.addBenchmarksToComparison(selectedBenchmarks).then(() => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      this.setState({
        redirect: {
          pathname: NAV_PATHS.COMPARISON,
          state: { from: location },
        },
      });
    });
  };

  onAddBenchmarksToPortfolio = () => {
    const {
      benchmark: { selectedBenchmarks },
    } = this.props;

    this.props.loadNewBenchmark();

    this.props.addBenchmarksToPortfolio(selectedBenchmarks).then(() => {
      this.setState({ redirect: '/models/custom-benchmarks?newBenchmarkPortfolio=true' });
    });
  };

  onLoadMoreResults = () => {
    const { selectedOption } = this.state;

    switch (selectedOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS:
      case ADVANCED_SEARCH_OPTIONS.PROXIES: {
        return this.props.loadMoreFundResults();
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        return this.props.loadMoreBenchmarkResults();
      }
      default: {
        return null;
      }
    }
  };

  renderSearchInput = () => {
    const {
      preLoadQuery,
      fund: { filtersData: fundFiltersData },
      minimizedFiltersToShow,
      benchmark: { filtersData: benchmarkFiltersData },
      loadingFilters,
      proxyOption,
      loadingResults,
    } = this.props;
    const loadingProps = { loadingFilters, loadingResults };
    const { selectedOption } = this.state;
    const hasData = this.getHasData(selectedOption, this.props.fund?.funds?.length || 0, this.props.benchmark?.benchmarks?.length || 0);

    switch (selectedOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS: {
        return (
          <FundSearchInput
            {...loadingProps}
            clearPreLoadQuery={this.props.setPreloadQuery}
            preLoadQuery={preLoadQuery}
            filtersData={fundFiltersData}
            onFundSearch={this.onFundSearch}
            minimizedFiltersToShow={minimizedFiltersToShow}
            user={this.props.user}
            hasData={hasData || loadingResults}
          />
        );
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        return (
          <BenchmarkSearchInput
            {...loadingProps}
            clearPreLoadQuery={this.props.setPreloadQuery}
            preLoadQuery={preLoadQuery}
            filtersData={benchmarkFiltersData}
            onBenchmarkSearch={this.onBenchmarkSearch}
            hasData={hasData || loadingResults}
          />
        );
      }
      case ADVANCED_SEARCH_OPTIONS.PROXIES: {
        return (
          <ProxySearchInput
            {...loadingProps}
            clearPreLoadQuery={this.props.setPreloadQuery}
            preLoadQuery={preLoadQuery}
            onProxySearch={this.onProxySearch}
            proxyOption={proxyOption}
            hasData={hasData || loadingResults}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  renderSearchResults = () => {
    const {
      useWindowScrollAncestor,
      fund: fundState,
      benchmark: benchmarkState,
      insideModal,
      loadingResults,
      savedFunds,
      savedBenchmarks,
      totalCount,
      hasMore,
      compact,
      canSelect,
      customTableActions,
      showInlineAdd,
      onAddItem,
      onCompareAlternatives,
      addingToPortfolio,
      addingToComparison,
      user,
    } = this.props;
    const { selectedOption } = this.state;

    const sharedProps = {
      classicVersionOnly: this.props.classicVersionOnly,
      insideModal,
      loading: loadingResults,
      onLoadMoreResults: this.onLoadMoreResults,
      hasMore,
      totalResultsCount: totalCount,
      useWindowScrollAncestor,
    };

    const tableColumns = TABLE_COLUMNS_MAP[selectedOption] || [];

    switch (selectedOption) {
      case ADVANCED_SEARCH_OPTIONS.FUNDS:
      case ADVANCED_SEARCH_OPTIONS.PROXIES: {
        return (
          <FundSearchResult
            {...sharedProps}
            addingToPortfolio={addingToPortfolio}
            addingToComparison={addingToComparison}
            fundState={fundState}
            onStartComparison={this.onStartComparison}
            onSortingChange={this.onSortingChange}
            onToggleSaved={this.onToggleSavedFund}
            savedFunds={savedFunds}
            onAddToPortfolio={this.onAddFundsToPortfolio}
            setFocusedFund={this.props.setFocusedFund}
            compact={compact}
            canSelect={canSelect}
            hideCompareAllAlternatives={this.props.hideCompareAllAlternatives}
            hideSaveFund={!userHasPermission(user, PERMISSIONS.SECTION_SEARCH)}
            focusOnRowClick={this.props.onRowClick === undefined}
            onRowClick={this.props.onRowClick}
            customTableActions={customTableActions}
            showInlineAdd={showInlineAdd}
            onAddItem={onAddItem}
            onClearComplianceAlternatives={this.props.clearComplianceAlternatives}
            onCompareAlternatives={onCompareAlternatives}
            searchQuery={this.props.searchQuery}
            tableColumns={tableColumns}
            // Legacy props
            overrideShowArrow={this.props.overrideShowArrow}
            onArrowClick={this.props.onArrowClick}
            user={user}
          />
        );
      }
      case ADVANCED_SEARCH_OPTIONS.BENCHMARKS: {
        return (
          <BenchmarkSearchResult
            {...sharedProps}
            savedBenchmarks={savedBenchmarks}
            benchmarkState={benchmarkState}
            onSortingChange={this.onSortingChange}
            setSelectedBenchmarks={this.props.setSelectedBenchmarks}
            onToggleSaved={this.onToggleSavedBenchmark}
            onStartComparison={this.onStartBenchmarksComparison}
            onAddToPortfolio={this.onAddBenchmarksToPortfolio}
            searchQuery={this.props.searchQuery}
            tableColumns={tableColumns}
            hideSaveFund={!userHasPermission(user, PERMISSIONS.SECTION_SEARCH)}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  render() {
    const { insideModal, hideSearchOptions } = this.props;
    const { redirect, selectedOption } = this.state;

    if (redirect) {
      return <Redirect to={redirect} />;
    }

    const classes = classNames('advanced-search d-flex flex-column', {
      'inside-modal': insideModal,
    });

    const searchOptions = this.getSearchOptions();

    return (
      <div className={classes}>
        <form role="search" className="advanced-search-input-container" onSubmit={e => e.preventDefault()}>
          {searchOptions.length > 1 && !hideSearchOptions && (
            <Tabs className="tabs-v2" onChange={this.onChangeOption} selected={selectedOption}>
              {searchOptions.map(option => (
                <ControlItem key={option.value} value={option.value}>
                  <FormattedMessage id={option.i18nKey} />
                </ControlItem>
              ))}
            </Tabs>
          )}

          {this.renderSearchInput()}
        </form>
        {/*
          Always keep search results rendered so that it keeps its
          own state between searches (ie: when sorting changes).
        */}
        {this.renderSearchResults()}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
  loadingResults: state.advancedSearch.loadingResults,
  loadingFilters: state.advancedSearch.loadingFilters,
  fund: selectAdvancedSearchShelfStatusedFund(state.advancedSearch),
  benchmark: state.advancedSearch.benchmark,
  savedFunds: state.funds.savedFunds.funds,
  savedBenchmarks: state.comparisonBenchmarks.savedBenchmarks.benchmarks,
  totalCount: state.advancedSearch.totalCount,
  preLoadQuery: state.advancedSearch.preLoadQuery,
  searchQuery: state.advancedSearch.searchOptions?.query,
  hasMore: state.advancedSearch.hasMore,
  addingToPortfolio: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  addingToComparison: selectIsLoadingEvery(state, [ADD_FUNDS_TO_COMPARISON.type]),
});

export default withLegacyVersion(
  connect(mapStateToProps, {
    loadSearchFilters,
    searchAllFunds,
    searchAllBenchmarks,
    updateSearchSorting,
    toggleSavedFund,
    resetSearch,
    clearResults,
    addFundsToComparison,
    resetPortfolio,
    setFocusedFund,
    addFundsToPortfolio,
    loadMoreFundResults,
    setPreloadQuery,
    addSearchItemLog,
    setSelectedBenchmarks,
    loadMoreBenchmarkResults,
    setFocusedBenchmark,
    toggleSavedBenchmark,
    addBenchmarksToComparison,
    addBenchmarksToPortfolio,
    loadNewBenchmark,
    clearComplianceAlternatives,
  })(withErrorBoundary(AdvancedSearch)),
  LegacyAdvancedSearch,
);
